import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CartContext = createContext(null);

const STORAGE_KEY = 'omg_cart';

const TIER_RANK = { diagnostic: 1, sovereign: 2, apex: 3 };

function hasAccess(userTier, requiredTier) {
  if (!requiredTier) return true;
  if (!userTier) return false;
  return (TIER_RANK[userTier] || 0) >= (TIER_RANK[requiredTier] || 0);
}

function loadCart() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveCart(items) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(loadCart);
  const [isOpen, setIsOpen] = useState(false);
  const [invitationCode, setInvitationCode] = useState('');

  useEffect(() => {
    saveCart(items);
  }, [items]);

  const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);
  const total = items.reduce((sum, i) => sum + i.amount * i.quantity, 0);

  const addItem = useCallback((item) => {
    setItems(prev => {
      // Tier items are exclusive — adding a tier replaces any previous tier
      if (item.type === 'tiers') {
        const withoutTiers = prev.filter(i => i.type !== 'tiers');
        return [...withoutTiers, { ...item, quantity: 1 }];
      }

      // Check if item already in cart
      const existing = prev.findIndex(i => i.type === item.type && i.id === item.id);
      if (existing !== -1) {
        const updated = [...prev];
        updated[existing] = { ...updated[existing], quantity: updated[existing].quantity + 1 };
        return updated;
      }

      return [...prev, { ...item, quantity: 1 }];
    });
    setIsOpen(true);
  }, []);

  const removeItem = useCallback((type, id) => {
    setItems(prev => prev.filter(i => !(i.type === type && i.id === id)));
  }, []);

  const updateQuantity = useCallback((type, id, quantity) => {
    if (quantity <= 0) {
      setItems(prev => prev.filter(i => !(i.type === type && i.id === id)));
      return;
    }
    setItems(prev => prev.map(i =>
      i.type === type && i.id === id ? { ...i, quantity } : i
    ));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    setInvitationCode('');
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);

  // Check if an item can be added based on current cart tier + user tier
  const canAdd = useCallback((userTier, requiredTier) => {
    // If a tier is in the cart, use that for access check
    const cartTier = items.find(i => i.type === 'tiers');
    const effectiveTier = cartTier ? cartTier.id : userTier;
    return hasAccess(effectiveTier, requiredTier);
  }, [items]);

  return (
    <CartContext.Provider value={{
      items,
      itemCount,
      total,
      isOpen,
      invitationCode,
      setInvitationCode,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      openCart,
      closeCart,
      canAdd,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
